import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

/**
 * Modal card that surfaces the AI-derived seller intelligence for a seller
 * profile (canonical buyer personas, anti-personas, competitor map, pain
 * taxonomy, account disqualifiers, proof points, category pitch).
 *
 * Used by:
 *   - SellerProfileSetup: auto-opens after a seller save so the user can
 *     review/edit before any account research is run
 *   - RerunResearchPopup: "View seller intelligence" link so user sees the
 *     lens this run will use
 *
 * Two modes:
 *   - Pretty render (default) — chip lists for tags, table-ish for personas
 *   - JSON edit (advanced) — full JSON textarea, save commits as user edit
 */
const SellerIntelligenceCard = observer(({ sellerProfileId, sellerProfileName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [deriving, setDeriving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [intel, setIntel] = useState(null);
  const [derivedAt, setDerivedAt] = useState(null);
  const [userEdited, setUserEdited] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonErr, setJsonErr] = useState("");

  const load = useCallback(() => {
    if (!sellerProfileId) return;
    setLoading(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}`,
      method: "GET"
    }).then(res => {
      const d = res?.data;
      if (d && d.found) {
        setIntel(d.intelligence || null);
        setDerivedAt(d.derivedAt || null);
        setUserEdited(!!d.userEdited);
      } else {
        setIntel(null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [sellerProfileId]);

  useEffect(() => { load(); }, [load]);

  const derive = () => {
    if (!sellerProfileId || deriving) return;
    setDeriving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/derive`,
      method: "POST",
      data: {}
    }).then(res => {
      const d = res?.data;
      if (d && d.ok && d.intelligence) {
        setIntel(d.intelligence);
        setDerivedAt(d.derivedAt || new Date().toISOString());
        setUserEdited(false);
        toasterState.setToastMsg("Seller intelligence derived", "success");
      } else {
        toasterState.setToastMsg(`Derive failed: ${d?.reason || "unknown"}`, "failure");
      }
      setDeriving(false);
    }).catch(err => {
      toasterState.setToastMsg(`Derive failed: ${err?.message || "unknown"}`, "failure");
      setDeriving(false);
    });
  };

  const enterJsonMode = () => {
    setJsonText(JSON.stringify(intel || {}, null, 2));
    setJsonErr("");
    setJsonMode(true);
  };

  const saveJson = () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { setJsonErr(`Invalid JSON: ${e.message}`); return; }
    setSaving(true);
    makeApi({
      url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(sellerProfileId)}/update`,
      method: "POST",
      data: { intelligence: parsed }
    }).then(res => {
      const d = res?.data;
      if (d && d.ok) {
        setIntel(parsed);
        setUserEdited(true);
        setJsonMode(false);
        toasterState.setToastMsg("Saved", "success");
      } else {
        toasterState.setToastMsg(`Save failed: ${d?.reason || "unknown"}`, "failure");
      }
      setSaving(false);
    }).catch(err => {
      toasterState.setToastMsg(`Save failed: ${err?.message || "unknown"}`, "failure");
      setSaving(false);
    });
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Section = ({ title, children, count }) => (
    <div className="siCard__section">
      <div className="siCard__section-head">
        <h4>{title}</h4>
        {count != null && <span className="siCard__count">{count}</span>}
      </div>
      {children}
    </div>
  );

  const Chips = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) return <p className="siCard__empty">—</p>;
    return (
      <div className="siCard__chips">
        {items.map((s, i) => <span key={i} className="siCard__chip">{s}</span>)}
      </div>
    );
  };

  return (
    <div className="siCardOverlay" onClick={onClose}>
      <div className="siCard" onClick={(e) => e.stopPropagation()}>
        <div className="siCard__header">
          <div>
            <h3>🧠 Seller Intelligence</h3>
            <p className="siCard__sub">
              {sellerProfileName ? <>For <strong>{sellerProfileName}</strong></> : "Derived buyer personas, anti-personas & competitor map"}
              {derivedAt && <span className="siCard__age"> · derived {new Date(derivedAt).toLocaleString()}</span>}
              {userEdited && <span className="siCard__edited"> · user-edited</span>}
            </p>
          </div>
          <span className="siCard__close" onClick={onClose}>✕</span>
        </div>

        <div className="siCard__body">
          {loading && <div className="siCard__loading">Loading…</div>}

          {!loading && !intel && (
            <div className="siCard__empty-state">
              <p>No intelligence derived yet for this seller.</p>
              <button className="siCard__btn primary" onClick={derive} disabled={deriving}>
                {deriving ? "Deriving…" : "Derive now"}
              </button>
            </div>
          )}

          {!loading && intel && !jsonMode && (
            <>
              {intel.category_pitch_one_liner && (
                <div className="siCard__pitch">
                  <span className="siCard__pitch-label">Category pitch</span>
                  <p>{intel.category_pitch_one_liner}</p>
                </div>
              )}

              <Section title="✅ Canonical buyer personas" count={(intel.canonical_buyer_personas || []).length}>
                {(intel.canonical_buyer_personas || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__personas">
                    {(intel.canonical_buyer_personas || []).map((p, i) => (
                      <li key={i} className="siCard__persona">
                        <div className="siCard__persona-head">
                          <strong>{p.title}</strong>
                          {p.seniority && <span className="siCard__pill">{p.seniority}</span>}
                          {p.meddic_role_typical && <span className="siCard__pill siCard__pill--meddic">{p.meddic_role_typical}</span>}
                          {p.department && <span className="siCard__pill siCard__pill--dept">{p.department}</span>}
                        </div>
                        {p.why_this_persona && <p className="siCard__persona-why">{p.why_this_persona}</p>}
                        {Array.isArray(p.title_keywords) && p.title_keywords.length > 0 && (
                          <div className="siCard__chips siCard__chips--inline">
                            {p.title_keywords.map((k, j) => <span key={j} className="siCard__chip siCard__chip--key">{k}</span>)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="🚫 Anti-personas (never recommend)" count={(intel.anti_personas || []).length}>
                {(intel.anti_personas || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__antis">
                    {(intel.anti_personas || []).map((a, i) => (
                      <li key={i}><strong>{a.title_pattern}</strong>{a.why_not && <span> — {a.why_not}</span>}</li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="⚔️ Competitor map">
                <div className="siCard__cmap">
                  <div><span className="siCard__cmap-label siCard__cmap-label--direct">Direct (we replace)</span><Chips items={intel.competitor_map?.direct} /></div>
                  <div><span className="siCard__cmap-label siCard__cmap-label--adj">Adjacent (overlap)</span><Chips items={intel.competitor_map?.adjacent} /></div>
                  <div><span className="siCard__cmap-label siCard__cmap-label--comp">Complementary (integrate)</span><Chips items={intel.competitor_map?.complementary} /></div>
                </div>
              </Section>

              <Section title="📉 Pain taxonomy" count={(intel.pain_taxonomy || []).length}>
                {(intel.pain_taxonomy || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__pains">
                    {(intel.pain_taxonomy || []).map((p, i) => (
                      <li key={i}>
                        <strong>{p.pain}</strong>
                        {p.evidence_we_solve_it && <p>How we solve: {p.evidence_we_solve_it}</p>}
                        {p.language_to_use && <p className="siCard__lang">Their words: "<em>{p.language_to_use}</em>"</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="📡 Buying signals to watch" count={(intel.buying_signals_taxonomy || []).length}>
                {(intel.buying_signals_taxonomy || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__signals">
                    {(intel.buying_signals_taxonomy || []).map((s, i) => (
                      <li key={i}>
                        <strong>{s.signal_kind}</strong>
                        {s.urgency_default && <span className={`siCard__urg siCard__urg--${String(s.urgency_default).toLowerCase()}`}>{s.urgency_default}</span>}
                        {s.why_it_triggers_us && <p>{s.why_it_triggers_us}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="🛑 Account disqualifiers" count={(intel.account_disqualifiers || []).length}>
                <Chips items={intel.account_disqualifiers} />
              </Section>

              <Section title="🏆 Proof points" count={(intel.proof_points || []).length}>
                {(intel.proof_points || []).length === 0 ? <p className="siCard__empty">—</p> : (
                  <ul className="siCard__proof">
                    {(intel.proof_points || []).map((p, i) => (
                      <li key={i}>
                        <strong>{p.customer_segment}</strong>
                        {p.outcome_metric && <span> — {p.outcome_metric}</span>}
                        {p.source_hint && <span className="siCard__src"> ({p.source_hint})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}

          {!loading && intel && jsonMode && (
            <div className="siCard__json">
              <textarea
                className="siCard__json-area"
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setJsonErr(""); }}
                spellCheck={false}
              />
              {jsonErr && <p className="siCard__json-err">{jsonErr}</p>}
            </div>
          )}
        </div>

        <div className="siCard__footer">
          {!jsonMode ? (
            <>
              {intel && (
                <button className="siCard__btn ghost" onClick={enterJsonMode}>Edit as JSON</button>
              )}
              <button className="siCard__btn ghost" onClick={derive} disabled={deriving}>
                {deriving ? "Re-deriving…" : "Re-derive from latest pitch"}
              </button>
              <button className="siCard__btn primary" onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button className="siCard__btn ghost" onClick={() => setJsonMode(false)}>Cancel</button>
              <button className="siCard__btn primary" onClick={saveJson} disabled={saving}>
                {saving ? "Saving…" : "Save edits"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default SellerIntelligenceCard;
